/**
 * ORR-233 — Releer el padrón de ARCA para varios clientes de una.
 *
 * Tres estados: selección → corriendo → resumen. El resumen muestra los cuatro grupos
 * siempre, incluso en cero: el que no puede quedar en silencio es "con error", porque
 * un cliente que falló es indistinguible de uno que no cambió si solo se cuenta el total.
 */

import { useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Progress } from '@/components/ui/progress'
import { Loader2, RefreshCw, CheckCircle2, XCircle, MinusCircle, SearchX } from 'lucide-react'
import { toast } from 'sonner'
import {
  useSincronizarPadronMasivo,
  resumirResultados,
  type ClienteASincronizar,
  type ResultadoSincronizacion,
} from '@/hooks/useSincronizarPadronMasivo'

export interface ClienteMasivo extends ClienteASincronizar {
  esMonotributista: boolean
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  clientes: ClienteMasivo[]
}

type Etapa = 'seleccion' | 'corriendo' | 'resumen'

/** Un grupo del resumen. Se renderiza aunque esté vacío. */
function GrupoResultado({
  icono,
  titulo,
  descripcion,
  items,
  destacar,
}: {
  icono: React.ReactNode
  titulo: string
  descripcion: string
  items: ResultadoSincronizacion[]
  destacar?: boolean
}) {
  return (
    <div className={`rounded-md border p-3 ${destacar && items.length > 0 ? 'border-destructive/50 bg-destructive/5' : ''}`}>
      <div className="flex items-center gap-2 mb-1">
        {icono}
        <span className="font-medium text-sm">{titulo}</span>
        <Badge variant={destacar && items.length > 0 ? 'destructive' : 'secondary'}>{items.length}</Badge>
      </div>
      <p className="text-xs text-muted-foreground mb-2">{descripcion}</p>
      {items.length > 0 && (
        <ul className="text-xs space-y-1 max-h-32 overflow-y-auto">
          {items.map((r) => (
            <li key={r.clienteId} className="flex justify-between gap-2">
              <span className="truncate">{r.nombre}</span>
              <span className="text-muted-foreground shrink-0">
                {r.tipo === 'actualizado' && r.categoriaNueva ? `Cat. ${r.categoriaNueva}` : r.detalle || ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function SincronizarPadronMasivoDialog({ open, onOpenChange, clientes }: Props) {
  const [etapa, setEtapa] = useState<Etapa>('seleccion')
  const [incluirNoMonotributistas, setIncluirNoMonotributistas] = useState(false)
  const { sincronizar, cancelar, procesados, total, resultados } = useSincronizarPadronMasivo()

  // El caso de uso es la recategorización, que es de monotributo: el resto queda fuera
  // salvo que se pida explícitamente.
  const seleccionados = useMemo(
    () => clientes.filter((c) => (incluirNoMonotributistas ? true : c.esMonotributista)),
    [clientes, incluirNoMonotributistas]
  )

  const sinCuit = useMemo(() => seleccionados.filter((c) => !c.cuit).length, [seleccionados])
  const resumen = useMemo(() => resumirResultados(resultados), [resultados])
  const porcentaje = total > 0 ? (procesados / total) * 100 : 0

  const handleSincronizar = async () => {
    setEtapa('corriendo')
    try {
      const finales = await sincronizar(seleccionados)
      setEtapa('resumen')
      const actualizados = finales.filter((r) => r.tipo === 'actualizado').length
      toast.success(
        actualizados > 0
          ? `${actualizados} ${actualizados === 1 ? 'cliente actualizado' : 'clientes actualizados'} desde ARCA`
          : 'Ningún cliente cambió de categoría'
      )
    } catch (error) {
      setEtapa('resumen')
      toast.error(error instanceof Error ? error.message : 'Error al sincronizar')
    }
  }

  const handleCerrar = (abierto: boolean) => {
    if (!abierto && etapa === 'corriendo') return // no cerrar a mitad del lote
    if (!abierto) setEtapa('seleccion')
    onOpenChange(abierto)
  }

  return (
    <Dialog open={open} onOpenChange={handleCerrar}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Actualizar datos desde ARCA</DialogTitle>
          <DialogDescription>
            Relee del padrón la categoría de monotributo y las actividades, y actualiza los
            clientes cuyo dato haya cambiado.
          </DialogDescription>
        </DialogHeader>

        {etapa === 'seleccion' && (
          <div className="space-y-4">
            <div className="rounded-md border p-3 text-sm">
              <p>
                Se van a consultar <strong>{seleccionados.length}</strong>{' '}
                {seleccionados.length === 1 ? 'cliente' : 'clientes'}.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Tarda alrededor de {Math.max(1, Math.round((seleccionados.length * 1.5) / 60))} min.
                No cierres esta ventana mientras corre.
              </p>
              {sinCuit > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {sinCuit} sin CUIT cargado: van a figurar con error.
                </p>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={incluirNoMonotributistas}
                onCheckedChange={(v) => setIncluirNoMonotributistas(v === true)}
              />
              Incluir también a los que no son monotributistas
            </label>
          </div>
        )}

        {etapa === 'corriendo' && (
          <div className="space-y-3">
            <Progress value={porcentaje} className="h-3" />
            <p className="text-sm text-center text-muted-foreground">
              Consultando {procesados} de {total}...
            </p>
          </div>
        )}

        {etapa === 'resumen' && (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            <GrupoResultado
              icono={<CheckCircle2 className="h-4 w-4 text-primary" />}
              titulo="Actualizados"
              descripcion="ARCA devolvió una categoría distinta de la que estaba cargada."
              items={resumen.actualizados}
            />
            <GrupoResultado
              icono={<MinusCircle className="h-4 w-4 text-muted-foreground" />}
              titulo="Sin cambios"
              descripcion="El padrón coincide con lo que ya estaba."
              items={resumen.sinCambios}
            />
            <GrupoResultado
              icono={<SearchX className="h-4 w-4 text-muted-foreground" />}
              titulo="Sin datos en el padrón"
              descripcion="El CUIT no figura activo, o el padrón respondió sin datos fiscales."
              items={resumen.noEncontrados}
            />
            <GrupoResultado
              icono={<XCircle className="h-4 w-4 text-destructive" />}
              titulo="Con error"
              descripcion="No se pudo consultar o guardar. Estos quedaron sin actualizar."
              items={resumen.errores}
              destacar
            />
          </div>
        )}

        <DialogFooter>
          {etapa === 'seleccion' && (
            <>
              <Button variant="outline" onClick={() => handleCerrar(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSincronizar} disabled={seleccionados.length === 0}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Actualizar {seleccionados.length}
              </Button>
            </>
          )}
          {etapa === 'corriendo' && (
            <Button variant="outline" onClick={cancelar}>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Detener
            </Button>
          )}
          {etapa === 'resumen' && <Button onClick={() => handleCerrar(false)}>Cerrar</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
